import { Component, ElementRef, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import { SharedModule } from '@apps/landing/src/app/shared/shared.module';
import { ControlContainer, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { handleResponseMessage, RESPONSE_TYPE } from '@apps/landing/src/app/shared/utils/handle-response-message';

@Component({
  selector: 'app-file-upload',
  imports: [SharedModule, ReactiveFormsModule],
  standalone: true,
  templateUrl: './file-upload.component.html',
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: () => inject(ControlContainer, { skipSelf: true }),
    },
  ],
})
export class FileUploadComponent {
  @Input({ required: true }) control = '';
  @Input() maxSizeMB = 1;
  @Input() multiple = false;
  @Output() fileSelected = new EventEmitter<File | File[]>();
  @Output() onError = new EventEmitter<boolean>();
  @ViewChild('fileInput', { static: true }) fileInput!: ElementRef<HTMLInputElement>;

  parentContainer = inject(ControlContainer);

  private handleResponseMessage = handleResponseMessage();

  protected readonly maxFileNumber = 3;
  protected filePreviews: Record<string, { preview: string; file: File; isImage: boolean }[]> = {};

  get parentFormGroup(): FormGroup {
    return this.parentContainer.control as FormGroup;
  }

  get formControl(): FormControl {
    return this.parentFormGroup.get(this.control) as FormControl;
  }

  ngOnInit() {
    if (!this.parentFormGroup.contains(this.control)) {
      this.parentFormGroup.addControl(this.control, new FormControl(null));
    }
  }

  protected displayErrors(): boolean {
    const control = this.formControl;
    const hasError = control.invalid && (control.dirty || control.touched);
    this.onError.emit(hasError);
    return hasError;
  }

  protected onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const files = Array.from(input.files);
    const maxSizeBytes = this.maxSizeMB * 1024 * 1024;

    const validMimeTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const validExtensions = ['.pdf', '.docx'];

    const validFiles = files.filter(file => {
      const isValidType = validMimeTypes.includes(file.type);
      const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
      const isValidSize = file.size <= maxSizeBytes;

      if (!isValidType || !hasValidExtension) {
        this.handleResponseMessage(`Invalid file type: ${file.name}`, RESPONSE_TYPE.ERROR);
        return false;
      }

      if (!isValidSize) {
        this.handleResponseMessage(`File size must be less than ${this.maxSizeMB}MB: ${file.name}`, RESPONSE_TYPE.ERROR);
        return false;
      }

      return true;
    });

    if (!validFiles.length) {
      input.value = '';
      return;
    }

    const existingFiles = this.formControl.value ?? [];
    const existingCount = Array.isArray(existingFiles) ? existingFiles.length : 0;

    if (this.multiple) {
      const totalFiles = existingCount + validFiles.length;

      if (totalFiles > this.maxFileNumber) {
        this.handleResponseMessage(`You can only upload up to ${this.maxFileNumber} files.`, RESPONSE_TYPE.ERROR);
        input.value = '';
        return;
      }
    }

    if (this.multiple) {
      const updatedFiles = [...existingFiles, ...validFiles];
      this.filePreviews[this.control] = [
        ...(this.filePreviews[this.control] || []),
        ...validFiles.map(file => ({ file, preview: '', isImage: false })),
      ];
      this.formControl.setValue(updatedFiles);
      this.fileSelected.emit(updatedFiles);
    } else {
      this.filePreviews[this.control] = validFiles.map(file => ({ file, preview: '', isImage: false }));
      this.formControl.setValue(validFiles[0]);
      this.fileSelected.emit(validFiles[0]);
    }
  }

  protected removeFile(fileName: string): void {
    const previews = this.filePreviews[this.control];
    if (!previews) return;

    this.filePreviews[this.control] = previews.filter(p => p.file.name !== fileName);

    const currentFiles: File[] | File = this.formControl.value;

    if (Array.isArray(currentFiles)) {
      const updatedFiles = currentFiles.filter(file => file.name !== fileName);
      this.formControl.setValue(updatedFiles.length ? updatedFiles : null);
    } else if (currentFiles && (currentFiles as File).name === fileName) {
      this.formControl.setValue(null);
    }

    this.fileInput.nativeElement.value = '';
  }

  clear() {
    this.fileInput.nativeElement.value = '';
    this.formControl.setValue(null);
    delete this.filePreviews[this.control];
  }
}
